<template>
  <form
    class="modal-content"
    @submit.prevent="submit"
  >
    <div class="modal-content__wrapper">
      <h3 class="modal-title">Создание продажи</h3>
      <p class="modal-text">Выберите товар, продавца и способ оплаты</p>

      <search
        ref="search"
        :list="getProducts"
        :pre-selected-items="products"
        @update="selectProducts"
      />

      <div class="sale-total">
        <span>Итого:</span>

        <strong>{{ priceToFormat(totalPrice) }}</strong>
      </div>

      <div class="form-group">
        <label class="form-label">Продавец</label>

        <select
          v-model="form.userId"
          :disabled="isSeller"
          class="form-input"
        >
          <option
            value="-1"
            disabled
          >
            Выберите продавца
          </option>

          <option
            v-for="({ id, name }, index) in getUsers"
            :key="`user-${index}`"
            :value="id"
          >
            {{ name }}
          </option>
        </select>
      </div>

      <div class="form-group">
        <label class="form-label">Тип оплаты</label>

        <select
          v-model="form.typePay"
          class="form-input"
        >
          <option
            value="-1"
            disabled
          >
            Выберите способ оплаты
          </option>

          <option
            v-for="({ code, name }, index) in getTypesPayList"
            :key="`user-${index}`"
            :value="code"
          >
            {{ name }}
          </option>
        </select>
      </div>

      <multi-pay
        v-if="isMultiPay"
        :price="totalPrice"
        :terminal.sync="form.payCardTransfer"
        :cash.sync="form.payCash"
        :card-transfer.sync="form.payTerminal"
      />

      <div
        v-if="error"
        class="modal-error"
      >
        {{ error }}
      </div>
    </div>

    <div class="modal-actions">
      <button
        type="button"
        class="modal-button modal-button--cancel"
        :disabled="isSubmitting"
        @click="closeModal(null)"
      >
        Закрыть
      </button>

      <button
        type="submit"
        class="modal-button modal-button--confirm"
        :disabled="!canCreateSale || isSubmitting"
      >
        Создать продажу
      </button>
    </div>
  </form>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'

import Search from './components/search'
import MultiPay from './components/multiPay'

import { priceToFormat } from '@/helper'
import { setLocalData, clearLocalData, getLocalData } from '@/helper/localStorage'
import { SALE_NOW } from '@/constants/localStorage'

const PAY_TYPE_MAP = {
  'terminal': 'payTerminal',
  'cash': 'payCash',
  'cardTransfer': 'payCardTransfer'
}

export default {
  name: 'CreateSaleModal',
  props: {
    preSelectedProduct: {
      type: String,
      default: ''
    }
  },
  inject: {
    pastCodeData: {
      type: Object,
      default: () => ({})
    },
    clearPastCode: {
      type: Function,
      default: () => ({})
    }
  },
  components: {
    Search,
    MultiPay
  },
  data () {
    return {
      form: {
        productList: [],
        productListIdWithPrice: [],

        userId: -1,
        typePay: -1,

        payCardTransfer: 0,
        payCash: 0,
        payTerminal: 0
      },
      error: null,
      products: [],
      isSubmitting: false
    }
  },
  computed: {
    ...mapGetters('product', { getProducts: 'getData', getProductsObject: 'getDataObject', getDataObjectByCode: 'getDataObjectByCode' }),
    ...mapGetters('users', { getUsers: 'getData' }),
    ...mapGetters('user', { isSeller: 'isSeller', getUserData: 'getData' }),
    ...mapGetters('settings', ['getDefaultValues']),
    totalPrice () {
      if (!this.products) {
        return 0
      }

      return this.products.reduce((acc, { price }) => acc += Number(price) , 0)
    },
    canCreateSale () {
      const isMultiPay = this.isMultiPay && (
        this.form.terminal !== 0
        || this.form.cash !== 0
        || this.form.payCardTransfer !== 0
      ) || !this.isMultiPay

      return (
        this.form?.productList.length > 0
        && this.form.userId !== -1
        && this.form.typePay !== -1
        && isMultiPay
      )
    },
    getTypesPayList () {
      return this.getDefaultValues?.['type-pay'] || []
    },
    isMultiPay () {
      return this.form.typePay === 'multi'
    }
  },
  watch: {
    pastCodeData: {
      deep: true,
      handler ({ pastCodeList = [] } = {}) {
        this.setPastProduct(pastCodeList.at(0))
      }
    }
  },
  mounted () {
    this.seedData()
  },
  beforeDestroy () {
    this.saveSaleData()
  },
  methods: {
    ...mapActions('modal', ['closeModal']),
    ...mapActions('sale', ['create']),
    priceToFormat,
    selectProducts (products = []) {
      if (products.length === 0) {
        this.products = []
        this.form.productList = []
        this.form.productListIdWithPrice = []

        return
      }

      this.products = products
      this.form.productList = products.reduce((acc, { id }) => [...acc, id], [])
      this.form.productListIdWithPrice = products.reduce((acc, { id, price }) => [...acc, JSON.stringify({ id, price })], [])
    },
    setPastProduct (productCode) {
      if (!productCode) {
        return
      }

      this.clearPastCode()

      const product = this.getDataObjectByCode[productCode]

      if (!product?.id) {
        this.$notify({
          type: 'error',
          title: 'Товар не найден!',
          text: `Товар с кодом: ${productCode} не найден`,
          duration: 3000
        })

        return
      }

      this.products.push(product)
      this.$refs.search.hideDropdown()
    },
    seedData () {
      if (this.isSeller) {
        this.form.userId = this.getUserData.id
      }

      if (this.preSelectedProduct) {
        this.setPastProduct(this.preSelectedProduct)

        return
      }

      const saveData = getLocalData(SALE_NOW)

      if (saveData) {
        Object.entries(saveData).forEach(([key, value]) => {
          this.$set(this.form, key, value)
        })

        if (saveData.productList) {
          this.products.push(...saveData.productList.map(productId => this.getProductsObject[productId]))
        }

        this.clearSaleData()
      }
    },
    saveSaleData () {
      setLocalData(SALE_NOW, this.form)
    },
    clearSaleData () {
      clearLocalData(SALE_NOW)
    },
    async submit () {
      await this.$bus.emit('start-loader')
      this.isSubmitting = true

      if (!this.isMultiPay) {
        this.form[PAY_TYPE_MAP[this.form.typePay]] = this.totalPrice
      }

      const { data, error } = await this.create(this.form)

      await this.$bus.emit('stop-loader')
      this.isSubmitting = false

      if (error) {
        this.error = `Произошла ошибка: ${error.message}`

        return
      }

      setTimeout(() => {
        this.$notify({
          type: 'success',
          title: 'Ура!',
          text: `За эту продажу Вы, заработали: <span><b>${priceToFormat(data.salary)}<b/></span>`,
          clean: false,
          duration: 3000
        })
      }, 1000)

      this.updateSubData()

      this.$bus.emit('stop-loader')
      await this.closeModal(null)

      this.clearSaleData()
    },
    updateSubData () {
      if (this.$route.name !== 'dashboard') {
        return
      }

      this.$bus.emit('update-cash-summary')
      this.$bus.emit('update-sales-summary')
      this.$bus.emit('update-sale-history')
    }
  }
}
</script>

<style lang="scss" src="./style.scss" />
